import {
  getInternalArticleUrl,
  RelevantArticleLinkContext,
} from '../../../../utils/relevantArticleLinks';
import { AioRelevantArticleLink } from '../../types';

export type ResolvedRelatedLink = {
  id: string;
  url: string;
  label: string;
  /** Leaves the site, so the caller opens it in a new tab. */
  isExternal: boolean;
};

/**
 * Resolve one "Related Reading" entry into a URL and its visible label.
 *
 * Internal targets take their label from the linked entry, so the editor never
 * retypes an article title; only external links carry a manual one. Returns
 * undefined when either half is missing — a link with no destination or no text
 * is not renderable.
 *
 * Only the URL building is shared with the rest of the project
 * (`getInternalArticleUrl`); the branch selection is not, because this module's
 * Strapi component is shaped differently from `base-component.relevant-article`
 * — a `linkType` enum instead of branching on which field is present, and flat
 * external fields instead of a nested component. Aligning the two schemas would
 * let this collapse into `RelevantArticle.tsx`'s `buildLink`; that is a known,
 * deliberately deferred option, not an oversight.
 */
export const resolveRelatedLink = (
  link: AioRelevantArticleLink,
  context: RelevantArticleLinkContext,
): ResolvedRelatedLink | undefined => {
  const target = ((): { url?: string; label?: string } => {
    switch (link.linkType) {
      case 'internal_article':
        return {
          url: link.article
            ? getInternalArticleUrl(link.article, 'article', context)
            : undefined,
          label: link.article?.title,
        };
      case 'internal_page':
        return {
          url: link.page
            ? getInternalArticleUrl(link.page, 'page', context)
            : undefined,
          label: link.page?.title,
        };
      case 'external':
        return { url: link.externalUrl, label: link.externalTitle };
      default:
        return {};
    }
  })();

  const url = target.url?.trim();
  const label = target.label?.trim();

  return url && label
    ? { id: link.id, url, label, isExternal: link.linkType === 'external' }
    : undefined;
};
